import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MessageThread from "@/components/MessageThread";
import { Download, MessageSquare, Play } from "lucide-react";

export default function BuyerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [openThread, setOpenThread] = useState(null); // conversation_id
  const [unread, setUnread] = useState(0);
  const [streams, setStreams] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  const refreshConvs = () => api.get("/conversations").then(({ data }) => {
    setConversations(data);
    setUnread(data.reduce((s, c) => s + (c.unread_count || 0), 0));
  }).catch(() => {});

  useEffect(() => {
    if (user) {
      api.get("/orders/mine").then(({ data }) => setOrders(data));
      api.get("/tickets/mine").then(({ data }) => setTickets(data)).catch(() => {});
      api.get("/buyer/streams").then(({ data }) => setStreams(data)).catch(() => {});
      refreshConvs();
      const iv = setInterval(refreshConvs, 20000);
      return () => clearInterval(iv);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const startConversation = async (order_id, seller_id) => {
    try {
      const { data } = await api.post(`/conversations/for-order/${order_id}/seller/${seller_id}`);
      setConversations((cs) => cs.find((c) => c.conversation_id === data.conversation_id) ? cs : [data, ...cs]);
      setOpenThread(data.conversation_id);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not start conversation";
      const { toast } = await import("sonner");
      toast.error(msg);
    }
  };

  const downloadDigital = async (order_id, product_id, filename) => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/downloads/${order_id}/${product_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!r.ok) {
        const { toast } = await import("sonner");
        let msg = `Download failed (${r.status})`;
        try { const j = await r.json(); if (j.detail) msg = j.detail; } catch { /* not json */ }
        toast.error(msg);
        return;
      }
      const cd = r.headers.get("content-disposition") || "";
      const nameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
      const fname = decodeURIComponent(nameMatch?.[1] || filename || `download-${product_id}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.message || "Download failed");
    }
  };

  const openTicket = async (tid) => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/tickets/${tid}/download`, { headers: { Authorization: `Bearer ${token}` } });
      const html = await r.text();
      const w = window.open("", "_blank");
      w.document.write(html); w.document.close();
    } catch { /* noop */ }
  };

  return (
    <Layout>
      <section className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">/ Buyer</div>
        <h1 className="heading text-5xl font-black uppercase tracking-tight mt-2">Hello, {user.name}</h1>

        <div className="mt-10">
          <h2 className="heading text-2xl font-bold uppercase">Your streams</h2>
          <div className="mt-4 brut-border bg-white" data-testid="buyer-streams-block">
            {streams.length === 0 ? (
              <div className="p-12 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No streams yet</div>
            ) : (
              streams.map((s, i) => (
                <div key={s.product_id} data-testid={`stream-row-${s.product_id}`} className={`flex flex-wrap items-center gap-4 p-4 ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                  {s.image_url && <img src={s.image_url} alt="" className="w-16 h-16 brut-border object-cover" />}
                  <div className="flex-1 min-w-[200px]">
                    <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C]">{s.stream_type}</div>
                    <div className="heading font-bold">{s.title}</div>
                    <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555] mt-1">by {s.seller_name}</div>
                    {s.progress && s.progress.position_seconds > 5 && (
                      <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#1F3D2A] mt-1">
                        Left off in ch. {s.progress.chapter_index + 1} at {Math.floor(s.progress.position_seconds / 60)}:{String(Math.floor(s.progress.position_seconds % 60)).padStart(2, "0")}
                      </div>
                    )}
                  </div>
                  <a href={`/stream/${s.product_id}`} data-testid={`stream-open-${s.product_id}`} className="brut-border bg-[#C77E3C] text-white px-4 py-3 mono uppercase text-xs tracking-[0.25em] flex items-center gap-2 hover:bg-[#1F3D2A]">
                    <Play className="w-3 h-3" /> #Streamnow
                  </a>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="heading text-2xl font-bold uppercase">Your tickets</h2>
          <div className="mt-4 brut-border bg-white">
            {tickets.length === 0 ? (
              <div className="p-12 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No tickets yet</div>
            ) : (
              tickets.map((t, i) => (
                <div key={t.ticket_id} data-testid={`ticket-${t.ticket_id}`} className={`flex flex-wrap items-center gap-4 p-4 ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="heading font-bold">{t.event_name}</div>
                    <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555] mt-1">{t.event_location || "—"} · {t.event_date || "TBA"}</div>
                    <div className="mono text-xs mt-2 font-bold text-[#1F3D2A]">{t.ticket_number}</div>
                    <div className="text-xs text-[#555]">Holder: {t.buyer_full_name}</div>
                  </div>
                  <button data-testid={`open-ticket-${t.ticket_id}`} onClick={() => openTicket(t.ticket_id)} className="brut-border bg-[#C77E3C] text-white px-3 py-2 mono uppercase text-xs tracking-[0.2em] flex items-center gap-2 hover:bg-[#1F3D2A]"><Download className="w-3 h-3" /> Open ticket</button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="heading text-2xl font-bold uppercase">Your orders</h2>
          <div className="mt-4 brut-border bg-white">
            {orders.length === 0 ? (
              <div className="p-12 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No orders yet</div>
            ) : (
              orders.map((o, idx) => (
                <div key={o.order_id} data-testid={`order-${o.order_id}`} className={`p-5 ${idx > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="mono text-xs uppercase tracking-[0.25em] text-[#555]">{o.order_id}</div>
                      <div className="text-sm mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`pill-label ${o.payment_status === "paid" ? "bg-[#C77E3C]" : "bg-[#1F3D2A]"}`}>{o.payment_status}</span>
                      <span className="mono font-bold">${o.amount.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2">
                    {o.items.map((it) => (
                      <div key={`${it.product_id}-${it.selected_size || ""}-${it.selected_color || ""}`} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t-2 border-dashed border-[#EAEAEA] pt-2">
                        <div>
                          {it.title} <span className="text-[#555]">× {it.quantity}</span>
                          {it.delivery_mode === "pickup" && (
                            <span className="ml-2 mono text-[10px] uppercase tracking-[0.2em] text-[#C77E3C]">
                              · Local pickup {it.pickup_city ? `(${it.pickup_city}, ${it.pickup_country})` : ""}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {it.type === "digital" && o.payment_status === "paid" && (
                            <button
                              data-testid={`download-${it.product_id}`}
                              onClick={() => downloadDigital(o.order_id, it.product_id, it.title)}
                              className="brut-border bg-[#C77E3C] text-white px-3 py-1 mono uppercase text-xs tracking-[0.2em] flex items-center gap-2 hover:bg-[#1F3D2A]"
                            ><Download className="w-3 h-3" /> Download</button>
                          )}
                          {it.delivery_mode === "pickup" && o.payment_status === "paid" && (
                            <button data-testid={`message-seller-${o.order_id}-${it.seller_id}`} onClick={() => startConversation(o.order_id, it.seller_id)} className="brut-border bg-white px-3 py-1 mono uppercase text-xs tracking-[0.2em] flex items-center gap-2 hover:bg-[#1F3D2A] hover:text-white">
                              <MessageSquare className="w-3 h-3" /> Message seller
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {(conversations.length > 0 || openThread) && (
          <div className="mt-10" data-testid="buyer-messages-section">
            <h2 className="heading text-2xl font-bold uppercase flex items-center gap-3">
              Your messages
              {unread > 0 && (
                <span data-testid="buyer-unread-badge" className="brut-border bg-[#C77E3C] text-white px-3 py-1 mono text-[10px] uppercase tracking-[0.25em]">
                  {unread} new →
                </span>
              )}
            </h2>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="brut-border bg-white">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center text-[#555] mono uppercase text-xs tracking-[0.2em]">No conversations yet</div>
                ) : conversations.map((c, i) => (
                  <button key={c.conversation_id} data-testid={`conv-open-${c.conversation_id}`} onClick={() => { setOpenThread(c.conversation_id); setTimeout(refreshConvs, 800); }}
                    className={`w-full text-left p-4 hover:bg-[#FDF3E9] ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""} ${openThread === c.conversation_id ? "bg-[#FDF3E9]" : ""}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C]">Order {c.order_id.slice(-10)}</div>
                      {c.unread_count > 0 && <span data-testid={`unread-${c.conversation_id}`} className="brut-border bg-[#C77E3C] text-white px-2 py-0.5 mono text-[9px] uppercase tracking-[0.2em]">{c.unread_count} new</span>}
                    </div>
                    <div className="heading font-bold mt-1">{c.seller_name || c.seller_email}</div>
                    <div className="text-xs text-[#555] mt-1 truncate">{c.last_message_preview || "No messages yet"}</div>
                  </button>
                ))}
              </div>
              <div>
                {openThread ? (
                  <MessageThread conversationId={openThread} onClose={() => setOpenThread(null)} />
                ) : (
                  <div className="brut-border bg-white p-12 text-center mono text-xs uppercase tracking-[0.25em] text-[#555]">Pick a conversation to open →</div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
